import {Button, Flag, type FlagProps} from 'platform/components';
import {Box, Clickable, Grid, HStack, Heading, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {
  auctionClock,
  auctionSteps,
  auctionView,
  confirmationCaption,
  DEFAULT_BIDDING_DAYS,
  isAuctionAwarded,
  type AuctionStep,
  type ClockTone,
  type ConfirmationCaption,
  type AuctionStepStatus,
} from '../../model/auctionView';
import type {AuctionView, DateTimeString} from '../../model/auctionView.types';
import {useAuctionFormat} from './useAuctionFormat';
import {purchaseProgress, type PurchaseCase} from './usePurchaseCase';

function stateFlag(view: AuctionView): FlagProps {
  return match<AuctionView['state'], FlagProps>(view.state)
    .with('awaiting_vehicle', 'awaiting_pickup', 'delivered', () => ({
      label: i18n.t('entity.auction.header.awarded'),
      colorScheme: 'green',
    }))
    .with('draft', () => ({label: i18n.t('entity.auction.header.draft'), colorScheme: 'orange'}))
    .with('bidding', () => ({label: i18n.t('entity.auction.header.open'), colorScheme: 'blue'}))
    .with('decision', () => ({
      label: i18n.t('entity.auction.header.decisionWindow'),
      colorScheme: 'blue',
    }))
    .with('expired', () => ({
      label: i18n.t('entity.auction.header.decisionWindowEnded'),
      colorScheme: 'blue',
    }))
    .with('abandoned', () => ({
      label: i18n.t('entity.auction.state.abandoned'),
      colorScheme: 'red',
    }))
    .with('kept', () => ({label: i18n.t('entity.auction.state.kept'), colorScheme: 'neutral'}))
    .exhaustive();
}

const CONFIRMATION_CAPTION: Record<ConfirmationCaption, string> = {
  selectOffer: 'entity.auction.steps.selectOffer',
  selectAnotherOffer: 'entity.auction.confirmation.selectAnotherOffer',
  confirmNotify: 'entity.auction.confirmation.confirm',
  confirmBuyingLimit: 'entity.auction.steps.confirmBuyingLimit',
  agreeBuyingPrice: 'entity.auction.steps.agreeBuyingPrice',
  tradeInAgreement: 'entity.auction.steps.tradeInAgreement',
  winnerConfirmed: 'entity.auction.steps.winnerConfirmed',
};

function useStepCaptions(
  view: AuctionView,
  selectedOfferId: string | null,
  purchaseCase: PurchaseCase | null
) {
  const isAwarded = isAuctionAwarded(view);
  const preparation = i18n.t(
    match({isAwarded, isPublished: view.publishedAt !== null, isBidding: view.state === 'bidding'})
      .with({isAwarded: true}, () => 'entity.auction.header.awarded')
      .with({isPublished: true, isBidding: true}, () => 'entity.auction.steps.running')
      .with({isPublished: true}, () => 'entity.auction.steps.ended')
      .otherwise(() => 'entity.auction.steps.draft')
  );
  let offers = i18n.t('entity.auction.steps.afterPublication');
  if (view.publishedAt !== null) {
    offers = match({count: view.offers.length, isBidding: view.state === 'bidding'})
      .with({count: 0, isBidding: true}, () => i18n.t('entity.auction.steps.noOffersYet'))
      .with({count: 0}, () => i18n.t('entity.auction.steps.noOffers'))
      .otherwise(({count}) => i18n.t('entity.auction.steps.offerCount', {count}));
  }
  const confirmation = i18n.t(
    CONFIRMATION_CAPTION[confirmationCaption(view, selectedOfferId, purchaseProgress(purchaseCase))]
  );
  return {preparation, offers, confirmation} satisfies Record<AuctionStep, string>;
}

const CLOCK_COLOR: Record<ClockTone, FlagProps['colorScheme']> = {
  ok: 'green',
  soon: 'orange',
  overdue: 'red',
};

type Clock = ReturnType<typeof auctionClock>;

// Green above 24 h, amber below 24 h, red when overdue. A far deadline shows only its date.
function ClockFlag(props: {
  deadline: DateTimeString | null;
  now: Date;
  runningLabel: (clock: Clock) => string;
  overdueLabel: (clock: Clock) => string;
}) {
  const format = useAuctionFormat();
  if (props.deadline === null) {
    return null;
  }
  const clock = auctionClock(props.deadline, props.now);
  return (
    <>
      <Show when={!clock.isFar}>
        <Flag
          label={clock.tone === 'overdue' ? props.overdueLabel(clock) : props.runningLabel(clock)}
          colorScheme={CLOCK_COLOR[clock.tone]}
          isSubtle
          data-testid="auction-countdown"
        />
      </Show>
      <Text size="small" color="secondary">
        {format.dateTime(props.deadline)}
      </Text>
    </>
  );
}

const STEP_TITLE: Record<AuctionStep, string> = {
  preparation: 'entity.auction.steps.preparation',
  offers: 'entity.auction.steps.offers',
  confirmation: 'entity.auction.steps.confirmation',
};

function StepIcon(props: {status: AuctionStepStatus; isReachable: boolean; number: number}) {
  if (props.status === 'done') {
    return <Icon value="action/check_circle" color="severity.success" size={5} />;
  }
  if (props.isReachable) {
    return <Icon value="action/watch_later" color="severity.warning" size={5} />;
  }
  return (
    <Box
      width={5}
      height={5}
      flexShrink={0}
      borderRadius="circular"
      border="1px solid"
      borderColor="palettes.neutral.300.100"
    >
      <Text size="xSmall" align="center" color="secondary">
        {String(props.number)}
      </Text>
    </Box>
  );
}

interface AuctionHeaderProps {
  view: AuctionView;
  activeStep: AuctionStep;
  selectedOfferId: string | null;
  purchaseCase: PurchaseCase | null;
  onStepChange: (step: AuctionStep) => void;
  onEndAuction: () => void;
  onOpenNewRound: () => void;
  onAbandon: () => void;
  onKeepInStock: () => void;
  now?: Date;
}

export function AuctionHeader(props: AuctionHeaderProps) {
  const now = props.now ?? new Date();
  const captions = useStepCaptions(props.view, props.selectedOfferId, props.purchaseCase);
  const actions = auctionView(props.view).actions;
  const isActionShown = (name: string) =>
    actions.some(({action, isShown}) => action === name && isShown);
  const isActionAllowed = (name: string) =>
    actions.some(({action, allowed}) => action === name && allowed);
  const {biddingEndsAt, decisionDeadlineAt} = props.view.clocks;
  // After an early end the BE keeps a future deadline, so an expired round can still count down.
  const decisionFlag = (
    <ClockFlag
      deadline={decisionDeadlineAt}
      now={now}
      runningLabel={(clock) =>
        i18n.t('entity.auction.header.decisionIn', {days: clock.days, hours: clock.hours})
      }
      overdueLabel={() => i18n.t('entity.auction.header.decisionDeadlinePassed')}
    />
  );

  return (
    <VStack data-testid="auction-header">
      <Box padding={4}>
        <HStack spacing={4} align="center" justify="space-between" wrap>
          <VStack spacing={1}>
            <HStack spacing={2} align="center">
              <Heading size={3}>{i18n.t('entity.auction.labels.auction')}</Heading>
              {/* The BE creates the first round on publish. Before that, the draft is round 1 (v11). */}
              <Flag
                label={i18n.t('entity.auction.header.round', {number: props.view.roundNumber ?? 1})}
                colorScheme={props.view.publishedAt === null ? 'neutral' : 'blue'}
                isSubtle
                size="small"
              />
              <Flag {...stateFlag(props.view)} isSubtle size="small" data-testid="auction-state" />
            </HStack>
            <Show when={props.view.roundId}>
              {(roundId) => (
                <Text size="xSmall" color="secondary">
                  {i18n.t('entity.auction.header.auctionId', {id: roundId})}
                </Text>
              )}
            </Show>
          </VStack>
          <HStack spacing={3} align="center">
            {match(props.view.state)
              .with('draft', () => (
                <Text size="small" color="secondary">
                  {i18n.t('entity.auction.header.duration', {
                    count: props.view.biddingDays ?? DEFAULT_BIDDING_DAYS,
                  })}
                </Text>
              ))
              .with('bidding', () => (
                <>
                  <ClockFlag
                    deadline={biddingEndsAt}
                    now={now}
                    runningLabel={(clock) =>
                      i18n.t('entity.auction.header.endsIn', {days: clock.days, hours: clock.hours})
                    }
                    overdueLabel={(clock) =>
                      i18n.t('entity.auction.clock.overdue', {days: clock.days, hours: clock.hours})
                    }
                  />
                  <Show when={isActionShown('endBidding')}>
                    <Button
                      variant="danger"
                      title={i18n.t('entity.auction.header.endAuction')}
                      isDisabled={!isActionAllowed('endBidding')}
                      onClick={props.onEndAuction}
                      data-testid="end-auction"
                    />
                  </Show>
                </>
              ))
              .with('decision', () => decisionFlag)
              .with('expired', () => (
                <>
                  <Show when={isActionShown('relaunch')}>
                    <Button
                      variant="primary"
                      title={i18n.t('entity.auction.header.openNewRound')}
                      isDisabled={!isActionAllowed('relaunch')}
                      onClick={props.onOpenNewRound}
                      data-testid="open-new-round"
                    />
                  </Show>
                  <Show when={isActionShown('keepInStock')}>
                    <Button
                      variant="secondary"
                      title={i18n.t('entity.auction.expired.keepInStock')}
                      isDisabled={!isActionAllowed('keepInStock')}
                      onClick={props.onKeepInStock}
                      data-testid="auction-keep-in-stock"
                    />
                  </Show>
                  {decisionFlag}
                </>
              ))
              .otherwise(() => null)}
            <Show when={isActionShown('abandon')}>
              <Button
                variant="dangerOutlined"
                title={i18n.t('entity.auction.decision.abandon')}
                isDisabled={!isActionAllowed('abandon')}
                onClick={props.onAbandon}
                data-testid="auction-abandon"
              />
            </Show>
          </HStack>
        </HStack>
      </Box>
      <Box borderBottom="1px solid" borderColor="general.separator">
        <Grid columns={[1, 3]} spacing={0} data-testid="auction-steps">
          {auctionSteps(props.view, props.selectedOfferId).map(
            ({step, status, isReachable}, index) => {
              const isActive = step === props.activeStep;
              return (
                <Clickable
                  key={step}
                  isDisabled={!isReachable}
                  onClick={() => props.onStepChange(step)}
                  data-testid={`auction-step-${step}`}
                >
                  <Box
                    padding={3}
                    height="100%"
                    borderTop="3px solid"
                    borderColor={isActive ? 'general.accent' : 'general.transparent'}
                    backgroundColor={isActive ? 'general.white' : 'palettes.neutral.10.100'}
                    data-testid={`auction-step-${step}-${status}`}
                  >
                    <HStack spacing={2} align="flex-start">
                      <StepIcon status={status} isReachable={isReachable} number={index + 1} />
                      <VStack>
                        <Text alternative color={isReachable ? 'primary' : 'secondary'}>
                          {i18n.t(STEP_TITLE[step])}
                        </Text>
                        <Text size="xSmall" color="secondary">
                          {captions[step]}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                </Clickable>
              );
            }
          )}
        </Grid>
      </Box>
    </VStack>
  );
}
